import React, { useState, useEffect, useRef } from "react";
import { decimalToHoursMinutes, zeroToDash, parseHoursInput, decimalHoursToHHMM } from "../../../../utils/datetime";
import { hasPayoutChanged, updateHoursOverride } from "../../../../utils";

/**
 * HoursColumn component for displaying RT, OT, or Total hours with comparison tooltips
 * Shows differences between payout and original worker hours
 * Supports manual override when isEditing is true
 *
 * @param {Object} props Component props.
 * @param {Object} props.shift Shift row.
 * @param {"regular"|"overtime"|"doubletime"|"total"} props.hoursType Hours column type.
 * @param {boolean} props.isEditing Whether override inputs are shown.
 * @param {boolean} [props.isReopenedReview=false] Reopened-review tooltip/reference rows.
 * @param {string} [props.bgColor] Unused here; kept for row-color callers.
 * @param {number} props.dayIndex Day index in `shiftsToRender`.
 * @param {number} props.shiftIndex Shift index within the day.
 * @param {Function} props.setShiftsToRender Day-rows setter.
 * @param {string} [props.className=""] Extra cell classes.
 * @param {string} [props.fontWeight="text-sm"] Text-size class.
 * @param {boolean} [props.hoursMatch=true] Whether overridden hours match calculated hours.
 * @returns {JSX.Element|null}
 */
const HoursColumn = ({
  shift,
  hoursType, // 'regular', 'overtime', 'doubletime', or 'total'
  isEditing,
  isReopenedReview = false,
  bgColor,
  dayIndex,
  shiftIndex,
  setShiftsToRender,
  className = "",
  fontWeight = "text-sm", // Can be overridden for total column
  hoursMatch = true // Whether overridden hours match calculated hours
}) => {
  const [inputValue, setInputValue] = useState('');
  const prevIsEditingRef = useRef(isEditing);

  // Determine field names and values based on hours type
  /**
   * Field names and payout/worker/manager values for the current `hoursType`.
   *
   * @returns {Object|null} Hours field bundle, or null for an unknown type.
   */
  const getHoursData = () => {
    switch (hoursType) {
      case 'regular':
        return {
          payoutValue: shift.regularTimePayout,
          originalValue: shift.regularTime,
          managerReferenceValue: shift.regularTimeHoursLog,
          payoutField: 'regularTime',
          colSpan: 'col-span-2',
          borderClass: 'border-l-[2px] border-l-gray-400'
        };
      case 'overtime':
        return {
          payoutValue: shift.overtimePayout || 0,
          originalValue: shift.overtime || 0,
          managerReferenceValue: shift.overtimeHoursLog || 0,
          payoutField: 'overtime',
          colSpan: 'col-span-2',
          borderClass: 'border-l'
        };
      case 'doubletime':
        return {
          payoutValue: shift.doubleTimePayout || 0,
          originalValue: shift.doubleTime || 0,
          managerReferenceValue: shift.doubleTimeHoursLog || 0,
          payoutField: 'doubleTime',
          colSpan: 'col-span-2',
          borderClass: 'border-l'
        };
      case 'total':
        return {
          payoutValue: (Number(shift.regularTimePayout) || 0) + (Number(shift.overtimePayout) || 0) + (Number(shift.doubleTimePayout) || 0),
          originalValue: (Number(shift.regularTime) || 0) + (Number(shift.overtime) || 0) + (Number(shift.doubleTime) || 0),
          managerReferenceValue: (Number(shift.regularTimeHoursLog) || 0) + (Number(shift.overtimeHoursLog) || 0) + (Number(shift.doubleTimeHoursLog) || 0),
          payoutField: null, // Not used for total
          colSpan: 'col-span-2',
          borderClass: 'border-l'
        };
      default:
        return null;
    }
  };

  const hoursData = getHoursData();

  // Calculate values needed for useEffect (before early return)
  const payoutValue = hoursData?.payoutValue ?? 0;
  const overrideFieldMap = {
    'regular': 'regularTime',
    'overtime': 'overtime',
    'doubletime': 'doubleTime'
  };
  const overrideField = overrideFieldMap[hoursType];
  const hasOverride = overrideField && shift.hoursOverrides?.[overrideField] !== undefined;

  // Initialize input value when switching to editing mode or when payoutValue changes (if no override)
  // This hook must be called before any early returns
  useEffect(() => {
    const wasEditing = prevIsEditingRef.current;
    prevIsEditingRef.current = isEditing;

    if (isEditing && !wasEditing) {
      // When entering edit mode, show current payout value in hh:mm format
      setInputValue(payoutValue ? decimalHoursToHHMM(payoutValue) : '');
    } else if (!isEditing && wasEditing) {
      // When leaving edit mode, clear input
      setInputValue('');
    } else if (isEditing && wasEditing && !hasOverride) {
      // If still in edit mode and payoutValue changed (e.g., after clearing override), update input
      // Only update if there's no override (override values are managed by user input)
      setInputValue(payoutValue ? decimalHoursToHHMM(payoutValue) : '');
    }
  }, [isEditing, payoutValue, hasOverride]);

  // Early return after all hooks
  if (!hoursData) return null;

  const { originalValue, managerReferenceValue, payoutField, colSpan, borderClass } = hoursData;

  // Check if this is a payout-added shift (no id)
  const isPayoutAddedShift = shift.id === null || shift.id === undefined;

  // Check if there's a payout change
  const hasChange = hoursType === 'total'
    ? (hasPayoutChanged(shift, "regularTime") || hasPayoutChanged(shift, "overtime") || hasPayoutChanged(shift, "doubleTime"))
    : hasPayoutChanged(shift, payoutField);

  // Get text color class
  /**
   * Text color: blue for added/changed payout hours, red when total hours do not match.
   *
   * @returns {string}
   */
  const getTextColorClass = () => {
    // Payout-added shifts always show blue text
    if (isPayoutAddedShift) {
      return "text-blue-600";
    }
    // For total column, show red if hours don't match calculated hours
    if (hoursType === 'total' && !hoursMatch) {
      return "text-red-600";
    }
    return hasChange ? "text-blue-600" : "text-black";
  };

  // Handle input change - allow free typing, only format when input is complete
  /**
   * Store raw hours input while typing; formatting happens on blur.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Input change event.
   * @returns {void}
   */
  const handleInputChange = (e) => {
    const rawValue = e.target.value;

    // Allow empty input
    if (!rawValue || rawValue.trim() === '') {
      setInputValue('');
      return;
    }

    // If it's already in hh:mm format, allow it (user might be editing)
    if (/^\d{1,2}:\d{0,2}$/.test(rawValue)) {
      setInputValue(rawValue);
      return;
    }

    // While typing, just store the raw value without formatting
    // This prevents caret jumping and allows natural typing
    // Formatting will happen on blur
    setInputValue(rawValue);
  };

  // Handle input blur/enter - save override and format display
  /**
   * Parse the input, set or clear `hoursOverrides`, and format as hh:mm.
   * Skipped for the total column.
   *
   * @returns {void}
   */
  const handleInputBlur = () => {
    if (hoursType === 'total' || !setShiftsToRender || dayIndex === undefined || shiftIndex === undefined) {
      return;
    }

    const trimmed = inputValue.trim();

    if (trimmed === '') {
      // Clear override - the recalculation will update payoutValue, and useEffect will sync inputValue
      setShiftsToRender((prevData) =>
        updateHoursOverride(prevData, dayIndex, shiftIndex, overrideField, null)
      );
      // Reset input to show it will use calculated value
      setInputValue('');
    } else {
      // Parse and validate input
      const parsedValue = parseHoursInput(trimmed);
      if (parsedValue !== null && parsedValue >= 0 && parsedValue <= 24) {
        // Get the calculated value (what payoutValue would be without override)
        // If there's no override, payoutValue IS the calculated value
        // If there's an override, we need to recalculate, but for simplicity,
        // we'll compare against the current payoutValue and only set override if different
        const currentPayoutValue = Number(payoutValue) || 0;
        
        // Round both values to avoid floating point comparison issues
        const roundedParsed = Math.round(parsedValue * 60) / 60;
        const roundedCurrent = Math.round(currentPayoutValue * 60) / 60;
        
        // Only set override if the value is different from current payout
        // If same, clear the override (set to null) so it uses calculated value
        if (Math.abs(roundedParsed - roundedCurrent) < 0.001 && !hasOverride) {
          // Value matches current payout and there's no override, so no change needed
          // Just format the display
          setInputValue(decimalHoursToHHMM(parsedValue));
        } else if (Math.abs(roundedParsed - roundedCurrent) < 0.001 && hasOverride) {
          // Value matches current payout but there's an override, clear it
          setShiftsToRender((prevData) =>
            updateHoursOverride(prevData, dayIndex, shiftIndex, overrideField, null)
          );
          // Reset input to show it will use calculated value
          setInputValue('');
        } else {
          // Value is different, set override
          setShiftsToRender((prevData) =>
            updateHoursOverride(prevData, dayIndex, shiftIndex, overrideField, parsedValue)
          );
          // Format input to hh:mm for display
          setInputValue(decimalHoursToHHMM(parsedValue));
        }
      } else {
        // Invalid input, revert to current payout value in hh:mm format
        setInputValue(payoutValue ? decimalHoursToHHMM(payoutValue) : '');
      }
    }
  };

  // Handle Enter key
  /**
   * Commit on Enter; revert to the current payout value on Escape.
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} e Key event.
   * @returns {void}
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      // Revert to current value in hh:mm format
      setInputValue(payoutValue ? decimalHoursToHHMM(payoutValue) : '');
      e.target.blur();
    }
  };

  // Set payout hours directly from a tooltip reference value.
  /**
   * Set payout hours from a tooltip reference value (worker/manager hours).
   *
   * @param {number|string} value Hours to write as an override.
   * @returns {void}
   */
  const handleReferenceSelect = (value) => {
    if (
      hoursType === 'total' ||
      !isEditing ||
      !setShiftsToRender ||
      dayIndex === undefined ||
      shiftIndex === undefined
    ) {
      return;
    }
    setShiftsToRender((prevData) =>
      updateHoursOverride(prevData, dayIndex, shiftIndex, overrideField, Number(value) || 0)
    );
  };

  // Render comparison tooltip
  /**
   * Hover tooltip comparing payout vs worker/manager hours. In a reopened
   * review, reference values are clickable to apply as overrides.
   *
   * @returns {JSX.Element}
   */
  const renderComparisonTooltip = () => {
    if (isReopenedReview) {
      const tooltipClasses = "absolute z-10 shadow-lg text-base px-2 py-1 rounded text-xs whitespace-nowrap -top-20 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-100 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100";
      /**
       * One tooltip row; clickable when editing so the value becomes an override.
       *
       * @param {string} label Row label.
       * @param {number|string|null} value Hours value.
       * @returns {JSX.Element}
       */
      const renderReferenceRow = (label, value) => {
        const formattedValue = zeroToDash(decimalToHoursMinutes(value, true));
        const canSelect = isEditing && hoursType !== 'total' && value != null && value !== "";

        return (
          <div>
            {label}:{" "}
            {canSelect ? (
              <span
                className="cursor-pointer hover:underline font-semibold text-blue-600"
                onClick={() => handleReferenceSelect(value)}
              >
                {formattedValue}
              </span>
            ) : (
              <span className="font-semibold">{formattedValue}</span>
            )}
          </div>
        );
      };

      return (
        <div className={tooltipClasses}>
          <div>Saved data loaded (Click to Set):</div>
          {renderReferenceRow("Worker", originalValue)}
          {renderReferenceRow("Manager", managerReferenceValue)}
          {renderReferenceRow("Payout", payoutValue)}
          {/* Arrow pointing down */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"></div>
        </div>
      );
    }

    if (!hasChange) return null;

    const tooltipClasses = "absolute z-10 shadow-lg text-base px-2 py-1 rounded text-xs whitespace-nowrap -top-12 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-100 pointer-events-auto hover:opacity-100 hover:visible bg-gray-100";

    return (
      <div className={tooltipClasses}>
        <div>Worker Hours:</div>
        <div className="font-semibold">
          {zeroToDash(decimalToHoursMinutes(originalValue, true))}
        </div>
        {/* Arrow pointing down */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-100"></div>
      </div>
    );
  };

  const cellClasses = `${colSpan} ${bgColor} py-3 px-1 border-b ${borderClass} border-gray-300 flex items-center justify-center ${fontWeight} h-12 relative group ${getTextColorClass()} ${hasOverride ? 'bg-yellow-50 italic' : ''} ${isEditing && hoursType !== 'total' ? 'bg-white' : ''} ${className}`;

  return (
    <div className={cellClasses}>
      {isEditing && hoursType !== 'total' ? (
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className={`caret-black w-full h-8 text-center border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-300 rounded cursor-pointer ${getTextColorClass()}`}
          placeholder={decimalToHoursMinutes(payoutValue, true)}
          min="0"
          max="24"
        />
      ) : (
        <>
          {zeroToDash(decimalToHoursMinutes(payoutValue, true))}
        </>
      )}
      {renderComparisonTooltip()}
    </div>
  );
};

export default HoursColumn;